import {useCallback} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Box, Chip, IconButton, TableCell} from '@mui/material';

import {workforceDepartmentChipSx} from './workforceDepartmentChipSx';
import {WorkforceProfileRowScoreCell} from './WorkforceProfileRowScoreCell';
import {BasicTableCell, BasicTableRow} from '../../../../../components/BasicTable/BasicTableRow';
import {TextOverflowWithTooltip} from '../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../../lib/AppConfig';
import {transformDate} from '../../../../../utils/dateUtils/dateFormattingUtils';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import {WorkforceProfileTableRow} from '../../types/WorkforceProfileTypes';
import {getWorkforceProfileDisplayId} from '../../utils/WorkforceProfiles/getWorkforceProfileDisplayId';
import {isWorkforceProfileAnalysisInProgress} from '../../utils/WorkforceProfiles/workforceProfileStatusUtils';

export type Props = WorkforceProfileTableRow;

export const WorkforceProfileRow = ({
  resourceId,
  publicId,
  seniority,
  departmentLabel,
  targetProfileTitle,
  createdAt,
  status,
  alignment,
}: Props) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAnalysisInProgress = isWorkforceProfileAnalysisInProgress(status);
  const analysisInProgressLabel = String(t('workforceProfilesPage.list.table.analysisInProgress'));
  const seniorityLabel = getSeniorityLevelLabel(seniority, t);
  const displayId = getWorkforceProfileDisplayId({publicId, resourceId});

  const viewProfile = useCallback(() => {
    navigate(`${AppConfig.COMPANY_WORKFORCE_ANALYSIS_PROFILES_PATH}/${resourceId}${location.search}`);
  }, [location.search, navigate, resourceId]);

  return (
    <BasicTableRow rowVariant='flat' hover onClick={viewProfile} sx={{cursor: 'pointer'}}>
      <BasicTableCell>
        <Box component='code' sx={{fontSize: 12}}>
          {displayId}
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <Chip label={departmentLabel} size='small' sx={workforceDepartmentChipSx} />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip variant='body2' text={seniorityLabel} sx={{color: 'text.secondary'}} />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip variant='body2' text={targetProfileTitle} />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.overall}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.coreCompetencies}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.hardSkills}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.softSkills}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.responsibilities}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <WorkforceProfileRowScoreCell
          score={alignment?.culturalFit}
          isAnalysisInProgress={isAnalysisInProgress}
          analysisInProgressLabel={analysisInProgressLabel}
        />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          variant='body2'
          text={createdAt ? transformDate(createdAt, 'numeric') : '-'}
          sx={{color: 'text.secondary', fontSize: 12}}
        />
      </BasicTableCell>
      <TableCell
        align='right'
        sx={{borderBottom: `1px solid ${orangeColors[3]}`, py: 1, width: 1, whiteSpace: 'nowrap'}}
      >
        <IconButton
          size='small'
          aria-label='view profile'
          onClick={(event) => {
            event.stopPropagation();
            viewProfile();
          }}
        >
          <ArrowForwardIcon fontSize='small' />
        </IconButton>
      </TableCell>
    </BasicTableRow>
  );
};
